if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.isProgressGallery = this.dataset.desktopLayout === 'thumbnail';

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.isProgressGallery) {
          this.initProgressGallery();
        } else {
          this.indicatorLines = this.querySelectorAll('.product-gallery__indicator-line');
          const slides = this.getSlideItems();
          const activeMedia = slides.find((item) => item.classList.contains('is-active')) || slides[0];
          if (activeMedia) {
            this.updateIndicatorLines(activeMedia.dataset.mediaId);
          }
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      initProgressGallery() {
        this.progressFill = this.querySelector('.product-gallery__progress-fill');
        this.progressBar = this.querySelector('.product-gallery__progress');
        this.indicatorLines = this.querySelectorAll('.product-gallery__indicator-line');
        this.galleryPrev = this.querySelector('[data-gallery-nav="prev"]');
        this.galleryNext = this.querySelector('[data-gallery-nav="next"]');

        this.querySelectorAll('.product__media-item img').forEach((image) => {
          image.loading = 'eager';
        });

        if (this.galleryPrev) {
          this.galleryPrev.addEventListener('click', (event) => {
            event.preventDefault();
            this.goToRelativeMedia(-1);
          });
        }

        if (this.galleryNext) {
          this.galleryNext.addEventListener('click', (event) => {
            event.preventDefault();
            this.goToRelativeMedia(1);
          });
        }

        const slides = this.getSlideItems();
        const activeMedia = slides.find((item) => item.classList.contains('is-active')) || slides[0];
        if (activeMedia) {
          this.revealActiveMedia(activeMedia);
          this.updateProgressGallery(activeMedia.dataset.mediaId);
        }
      }

      getSlideItems() {
        return Array.from(this.elements.viewer.querySelectorAll('li.product__media-item[data-media-id]'));
      }

      goToRelativeMedia(direction) {
        const items = this.getSlideItems();
        let activeIndex = items.findIndex((element) => element.classList.contains('is-active'));
        if (activeIndex < 0) activeIndex = 0;

        const nextIndex = activeIndex + direction;
        if (nextIndex < 0 || nextIndex >= items.length) return;

        this.setActiveMedia(items[nextIndex].dataset.mediaId, false);
      }

      updateProgressGallery(mediaId) {
        if (!this.isProgressGallery) return;

        const items = this.getSlideItems();
        const activeIndex = items.findIndex((element) => element.dataset.mediaId === mediaId);
        if (activeIndex < 0) return;

        if (this.progressFill) {
          this.progressFill.style.setProperty('--progress-index', activeIndex);
        }

        if (this.progressBar) {
          this.progressBar.setAttribute('aria-valuenow', activeIndex + 1);
        }

        this.updateIndicatorLines(mediaId);

        if (this.galleryPrev) {
          if (activeIndex === 0) this.galleryPrev.setAttribute('disabled', 'disabled');
          else this.galleryPrev.removeAttribute('disabled');
        }

        if (this.galleryNext) {
          if (activeIndex >= items.length - 1) this.galleryNext.setAttribute('disabled', 'disabled');
          else this.galleryNext.removeAttribute('disabled');
        }
      }

      updateIndicatorLines(mediaId) {
        if (!this.indicatorLines?.length) return;

        const items = this.getSlideItems();
        const activeIndex = items.findIndex((element) => element.dataset.mediaId === mediaId);
        if (activeIndex < 0) return;

        this.indicatorLines.forEach((line, index) => {
          line.classList.toggle('is-active', index === activeIndex);
        });
      }

      onSlideChanged(event) {
        if (this.isProgressGallery) {
          if (!this.mql.matches && event.detail.currentElement) {
            this.setActiveMedia(event.detail.currentElement.dataset.mediaId, false);
          }
          return;
        }

        if (!this.mql.matches && event.detail.currentElement) {
          this.updateIndicatorLines(event.detail.currentElement.dataset.mediaId);
        }

        if (!this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const slides = this.getSlideItems();
        const activeMedia =
          slides.find((item) => item.dataset.mediaId === mediaId) || slides[0];
        if (!activeMedia) {
          return;
        }

        const previousActive = slides.find((item) => item.classList.contains('is-active'));
        activeMedia.classList.add('is-active');
        this.revealActiveMedia(activeMedia);

        if (previousActive && previousActive !== activeMedia) {
          previousActive.classList.remove('is-active');
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        if (!this.isProgressGallery) {
          window.setTimeout(() => {
            const useHorizontalScroll = !this.mql.matches || this.elements.thumbnails;
            if (useHorizontalScroll) {
              activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
            }
            const activeMediaRect = activeMedia.getBoundingClientRect();
            if (activeMediaRect.top > -0.5) return;
            const top = activeMediaRect.top + window.scrollY;
            window.scrollTo({ top: top, behavior: 'smooth' });
          });
        }

        this.playActiveMedia(activeMedia);

        if (this.isProgressGallery) {
          this.updateProgressGallery(mediaId);
        }

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);

        if (activeThumbnail) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      revealActiveMedia(activeMedia) {
        if (!activeMedia) return;

        activeMedia.querySelectorAll('.scroll-trigger').forEach((element) => {
          element.classList.add('scroll-trigger--cancel');
          element.classList.remove('scroll-trigger--offscreen');
        });

        activeMedia.querySelectorAll('img').forEach((image) => {
          image.loading = 'eager';
          if (!image.complete) {
            image.decode?.().catch(() => {});
          }
        });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
